/** Bridges treated as internet-facing unless the admin overrides the list. */
export const DEFAULT_EDGE_BRIDGES = ['vmbr0'];

export const DEFAULT_APP_NAME = 'Proxmon';

/** Mutating requests must carry this header; combined with SameSite it blocks CSRF. */
export const CSRF_HEADER = 'x-proxmon-csrf';
export const CSRF_VALUE = '1';

export const SESSION_COOKIE = 'proxmon_session';

/**
 * The versioned, read-stable prefix other programs should call.
 *
 * Everything under plain `/api` is the SPA's own wire and may change whenever
 * the UI does. Everything under this prefix is a promise: fields get added,
 * never removed or repurposed.
 */
export const API_V1_PREFIX = '/api/v1';

/** Scheme for the `Authorization` header an API token is sent with. */
export const API_TOKEN_SCHEME = 'Bearer';

export const MIN_PASSWORD_LENGTH = 12;

/**
 * How far back the backup timeline looks, unless PROXMON_BACKUP_WINDOW_HOURS
 * says otherwise. Two days covers "did last night run?" and "did the night
 * before?", which is the question the panel exists to answer.
 */
export const DEFAULT_BACKUP_WINDOW_HOURS = 48;

/**
 * How old a guest snapshot has to be before the map calls it stale, unless
 * PROXMON_SNAPSHOT_STALE_DAYS says otherwise.
 *
 * A week: long enough that a snapshot taken before this morning's upgrade is
 * not shouted about, short enough that one everybody has forgotten about is.
 */
export const DEFAULT_SNAPSHOT_STALE_DAYS = 7;

/** Normalise a MAC to lowercase colon-separated form for stable keying. */
export function normalizeMac(mac: string): string {
  return mac.trim().toLowerCase().replace(/-/g, ':');
}

const MAC_RE = /^[0-9a-f]{2}(:[0-9a-f]{2}){5}$/;

export function isMac(value: string): boolean {
  return MAC_RE.test(normalizeMac(value));
}

const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;

/**
 * Accepts a bare IPv4/IPv6 address or CIDR. Deliberately permissive about IPv6
 * (we only ever display these) but strict about IPv4 octet range.
 */
export function isIpAddress(value: string): boolean {
  const bare = value.trim().split('/')[0] ?? '';
  if (!bare) return false;
  if (IPV4_RE.test(bare)) {
    return bare.split('.').every((o) => {
      const n = Number(o);
      return n >= 0 && n <= 255 && String(n) === String(Number(o));
    });
  }
  return /^[0-9a-fA-F:]+$/.test(bare) && bare.includes(':');
}

/**
 * Parse an operator-typed link speed into bits per second.
 *
 * Accepts what someone actually types for a NIC: `1G`, `2.5 Gbps`, `10g`,
 * `1000M`, `100 mbit`, or a bare number of bits. Deliberately decimal — a
 * "1 Gigabit" NIC is 1,000,000,000 bits/s, not 2^30, and using binary units
 * here would quietly misreport saturation by 7%.
 *
 * Returns null for anything it cannot read, including zero and negatives.
 */
export function parseBitRate(input: string): number | null {
  const text = input.trim().toLowerCase();
  if (!text) return null;

  const match = /^([0-9]+(?:[.,][0-9]+)?)\s*([kmgt])?(?:b(?:it)?(?:\/s|ps)?)?$/.exec(text);
  if (!match) return null;

  const value = Number(match[1]?.replace(',', '.'));
  if (!Number.isFinite(value) || value <= 0) return null;

  const scale = { k: 1e3, m: 1e6, g: 1e9, t: 1e12 }[match[2] ?? ''] ?? 1;
  const bits = Math.round(value * scale);
  // A NIC faster than 1 Tb/s is a typo, not a link.
  return bits > 0 && bits <= 1e12 ? bits : null;
}

const BIT_UNITS = ['bit/s', 'kbit/s', 'Mbit/s', 'Gbit/s', 'Tbit/s'];

/** Render bits/sec the way a network engineer expects to read it. */
export function formatBitRate(bitsPerSec: number | null | undefined, digits = 1): string {
  if (bitsPerSec === null || bitsPerSec === undefined || !Number.isFinite(bitsPerSec)) return '—';
  if (bitsPerSec < 1) return '0 bit/s';
  const exponent = Math.min(BIT_UNITS.length - 1, Math.floor(Math.log10(bitsPerSec) / 3));
  const scaled = bitsPerSec / 1000 ** exponent;
  // Whole numbers read better without a trailing .0 — 1 Gbit/s, not 1.0 Gbit/s.
  const text = exponent === 0 || Number.isInteger(scaled) ? String(Math.round(scaled)) : scaled.toFixed(digits);
  return `${text} ${BIT_UNITS[exponent]}`;
}
